/**
 * Pause CSS animations + transitions + media + WAAPI + best-effort hooks
 * into popular animation libraries (GSAP, Lottie, Framer Motion).
 *
 * `freezeCanvas()` returns an `Unfreeze` callback. Reference-counted so
 * multiple call sites can stack freezes without stepping on each other.
 *
 * `getFreezeReport()` reports what was actually paused — the toolbar
 * surfaces this as the "fully frozen" indicator.
 */
const STYLE_ID = "__hovercontext_freeze_style__";
let counter = 0;
const handles = new Set();
let pausedMedia = null;
let pausedAnimations = [];
let pausedLibraries = [];
let lastReport = {
    active: false,
    waapi: 0,
    media: 0,
    libraries: [],
};
const buildStyleSheet = (options) => {
    const rules = [];
    if (options.pauseAnimations) {
        rules.push("animation-play-state: paused !important");
    }
    if (options.pauseTransitions) {
        rules.push("transition: none !important");
    }
    const universal = rules.length > 0
        ? `*, *::before, *::after { ${rules.join("; ")}; }`
        : "";
    const scroll = options.preventScroll
        ? "html, body { overflow: hidden !important; }"
        : "";
    return [universal, scroll].filter(Boolean).join("\n");
};
const ensureStyle = (options) => {
    let style = document.getElementById(STYLE_ID);
    if (!style) {
        style = document.createElement("style");
        style.id = STYLE_ID;
        document.head.appendChild(style);
    }
    style.textContent = buildStyleSheet(options);
};
const removeStyle = () => {
    document.getElementById(STYLE_ID)?.remove();
};
const pauseAllMedia = () => {
    const seen = new WeakSet();
    let count = 0;
    document
        .querySelectorAll("video, audio")
        .forEach((media) => {
        if (!media.paused) {
            seen.add(media);
            count += 1;
            try {
                media.pause();
            }
            catch {
                /* readyState may not permit */
            }
        }
    });
    pausedMedia = seen;
    return count;
};
const resumePausedMedia = () => {
    if (!pausedMedia)
        return;
    document
        .querySelectorAll("video, audio")
        .forEach((media) => {
        if (pausedMedia.has(media)) {
            media.play().catch(() => {
                /* user-gesture restrictions */
            });
        }
    });
    pausedMedia = null;
};
const pauseAllWAAPI = () => {
    if (typeof document.getAnimations !== "function")
        return 0;
    const animations = document.getAnimations();
    pausedAnimations = [];
    for (const animation of animations) {
        if (animation.playState === "running") {
            try {
                animation.pause();
                pausedAnimations.push(animation);
            }
            catch {
                /* readonly etc. */
            }
        }
    }
    return pausedAnimations.length;
};
const resumeAllWAAPI = () => {
    for (const animation of pausedAnimations) {
        try {
            animation.play();
        }
        catch {
            /* ignore */
        }
    }
    pausedAnimations = [];
};
const pauseKnownLibraries = () => {
    const paused = [];
    const w = window;
    try {
        if (w.gsap?.globalTimeline?.pause) {
            w.gsap.globalTimeline.pause();
            paused.push("gsap");
        }
    }
    catch {
        /* ignore */
    }
    try {
        if (w.lottie?.freeze) {
            w.lottie.freeze();
            paused.push("lottie");
        }
    }
    catch {
        /* ignore */
    }
    // Framer Motion / Motion One don't expose a global pause API, but
    // their underlying WAAPI animations are caught by `pauseAllWAAPI`.
    // Detect them so the report reflects coverage.
    try {
        if (document.querySelector("[data-projection-id]")) {
            paused.push("framer-motion (via WAAPI)");
        }
    }
    catch {
        /* ignore */
    }
    pausedLibraries = paused;
    return paused;
};
const resumeKnownLibraries = () => {
    const w = window;
    try {
        w.gsap?.globalTimeline?.resume?.();
    }
    catch {
        /* ignore */
    }
    try {
        w.lottie?.unfreeze?.();
    }
    catch {
        /* ignore */
    }
    pausedLibraries = [];
};
export const freezeCanvas = (options = {}) => {
    const resolved = {
        pauseAnimations: options.pauseAnimations ?? true,
        pauseTransitions: options.pauseTransitions ?? true,
        pauseMedia: options.pauseMedia ?? true,
        preventScroll: options.preventScroll ?? false,
        pauseWAAPI: options.pauseWAAPI ?? true,
        pauseLibraries: options.pauseLibraries ?? true,
    };
    const handle = { id: ++counter, active: true };
    const isFirst = handles.size === 0;
    handles.add(handle);
    if (isFirst) {
        ensureStyle(resolved);
        const mediaCount = resolved.pauseMedia ? pauseAllMedia() : 0;
        const waapiCount = resolved.pauseWAAPI ? pauseAllWAAPI() : 0;
        const libs = resolved.pauseLibraries ? pauseKnownLibraries() : [];
        lastReport = {
            active: true,
            waapi: waapiCount,
            media: mediaCount,
            libraries: libs,
        };
    }
    return () => {
        if (!handle.active)
            return;
        handle.active = false;
        handles.delete(handle);
        if (handles.size === 0) {
            removeStyle();
            resumePausedMedia();
            resumeAllWAAPI();
            resumeKnownLibraries();
            lastReport = { active: false, waapi: 0, media: 0, libraries: [] };
        }
    };
};
export const isCanvasFrozen = () => handles.size > 0;
export const getFreezeReport = () => lastReport;
//# sourceMappingURL=freeze.js.map